import {
  CAMINO_DE_MARCA, ESTADO_RIVAL, ESTADO_SKU, GATE, GATE_MEDIDA, GONDOLA_DE_MARCA,
  SKU_ESTA, SKU_NO_ESTA, ladoDeMarca, type GondolaYi,
} from './catalogo';
import {
  brecha, contar, contarEnOrden, resumirBrechas, type Conteo, type ResumenBrechas,
} from './comun';
import {
  arr, en, num, obj, str, strs, type FichaYiModerno, type Json, type MarcaCliente, type Presencia,
} from './tipos';

/**
 * La aritmética del canal moderno de Yichang: la góndola de snacks (Pringles), la de cereales
 * (Kellogg's), la vuelta por la tienda y la zona de cajas.
 *
 * Todo sale de la ficha tal como la arma `fichaLimpia` en `app-moderno/index.html`. Donde el
 * formulario calcula algo en pantalla (el reparto del espacio, el precio por 100 g, la brecha del
 * choque), acá se calcula **con la misma fórmula**: si la bandeja y el celular dieran números
 * distintos para la misma ficha, el supervisor que corrigió un dígito en la tienda vería otro
 * número en la oficina.
 */

export const gondolaDe = (f: FichaYiModerno, g: GondolaYi): Json | null => obj(f.ficha[g.id]);
export const puertaDe = (gd: Json | null): string | null => str(gd?.m1_se_pudo_medir);
/** «Completa» o «A medias»: la góndola se abrió y tiene el resto de las preguntas. */
export const gondolaMedida = (gd: Json | null): boolean => GATE_MEDIDA.includes(puertaDe(gd) ?? '');
export const estadoSku = (gd: Json | null, k: string): string | null =>
  str(obj(gd?.m5_estado_sku)?.[k]);

// ═══════════════════════════════════════════════════════════════════════════
// El espacio (2.7 · 3.6)
// ═══════════════════════════════════════════════════════════════════════════

export interface EspacioYi {
  cols: number;
  filas: number;
  marcas: string[];
  grid: string;
  /** El nombre de la opción «No cuenta»: celdas del mueble que no son de la categoría. */
  noCuenta: string | null;
}

export function leerEspacio(gd: Json | null): EspacioYi | null {
  const e = obj(gd?.m4_espacio);
  if (e === null) return null;
  const cols = num(e.cols);
  const filas = num(e.filas);
  const marcas = strs(e.marcas);
  if (cols === null || filas === null || marcas.length === 0) return null;
  return { cols, filas, marcas, grid: typeof e.grid === 'string' ? e.grid : '', noCuenta: str(e.no_cuenta) };
}

export interface RepartoMarca { marca: string; digito: number; celdas: number; pct: number }
export interface Reparto {
  /** `false` cuando no se puede calcular. Ahí no hay porcentaje: no hay un cero. */
  medida: boolean;
  motivo?: 'sin_grilla' | 'largo_no_cuadra' | 'nada_pintado' | 'solo_no_cuenta';
  /** Sin la opción «No cuenta». */
  marcas: RepartoMarca[];
  pintadas: number;
  noCuenta: number;
  /** El denominador del reparto: lo pintado menos lo que «No cuenta». */
  base: number;
  celdas: number;
  pctPintado: number;
  digitoNoCuenta: number | null;
}

/**
 * Cuánto ocupa cada marca, **con la misma fórmula del formulario**: sobre las celdas pintadas y sin
 * las marcadas «No cuenta» (la avena en cereales, las galletas en snacks). Si entraran, una góndola
 * compartida con otra categoría le bajaría la participación a todas las marcas sin que ninguna
 * hubiera perdido un centímetro.
 *
 * Con el redondeo la suma puede dar 99 o 101 y **no se fuerza a 100**: el punto que habría que
 * regalarle a alguna marca sería inventado.
 */
export function reparto(esp: EspacioYi | null): Reparto {
  const vacio: Reparto = {
    medida: false, marcas: [], pintadas: 0, noCuenta: 0, base: 0, celdas: 0, pctPintado: 0,
    digitoNoCuenta: null,
  };
  if (esp === null) return { ...vacio, motivo: 'sin_grilla' };
  const celdas = esp.cols * esp.filas;
  const idx = esp.noCuenta === null ? -1 : esp.marcas.indexOf(esp.noCuenta);
  const digitoNoCuenta = idx < 0 ? null : idx + 1;
  if (esp.grid === '') return { ...vacio, celdas, digitoNoCuenta, motivo: 'sin_grilla' };
  // Cambiar módulos o repisas en el formulario borra la grilla: un largo que no cuadra es un estado
  // real, y se dice en vez de calcular sobre celdas que ya no corresponden al mueble.
  if (esp.grid.length !== celdas) return { ...vacio, celdas, digitoNoCuenta, motivo: 'largo_no_cuadra' };

  const cuenta = new Map<string, number>();
  for (const ch of esp.grid) cuenta.set(ch, (cuenta.get(ch) ?? 0) + 1);
  const pintadas = celdas - (cuenta.get('0') ?? 0);
  const noCuenta = digitoNoCuenta === null ? 0 : (cuenta.get(String(digitoNoCuenta)) ?? 0);
  const base = pintadas - noCuenta;
  const pctPintado = celdas === 0 ? 0 : Math.round((pintadas / celdas) * 100);
  const comun = { pintadas, noCuenta, base, celdas, pctPintado, digitoNoCuenta };
  if (pintadas === 0) return { ...vacio, ...comun, motivo: 'nada_pintado' };
  if (base === 0) return { ...vacio, ...comun, motivo: 'solo_no_cuenta' };

  const marcas = esp.marcas
    .map((marca, i) => ({ marca, digito: i + 1 }))
    .filter((m) => m.digito !== digitoNoCuenta)
    .map((m) => {
      const n = cuenta.get(String(m.digito)) ?? 0;
      return { ...m, celdas: n, pct: Math.round((n / base) * 100) };
    });
  return { medida: true, marcas, ...comun };
}

/** La participación de la marca del cliente en su góndola, o `null` si no se pintó. */
export function participacionCliente(gd: Json | null, g: GondolaYi): number | null {
  const r = reparto(leerEspacio(gd));
  if (!r.medida) return null;
  return r.marcas.find((m) => m.marca === g.marcaNom)?.pct ?? 0;
}

// ═══════════════════════════════════════════════════════════════════════════
// El precio (2.24 a 2.29 · 3.26 a 3.31)
// ═══════════════════════════════════════════════════════════════════════════

export const GR_PACK = 'Pack';
export const GR_OTRO = 'Otro · lo escribo';

/** Los gramos de un texto como «104 g» o «1,2 kg». La misma expresión del formulario. */
export function gramosDeTexto(t: unknown): number | null {
  const m = String(t ?? '').replace(',', '.').match(/(\d+(?:\.\d+)?)\s*(kg|g)?/i);
  if (!m) return null;
  let n = parseFloat(m[1]);
  if (m[2] !== undefined && m[2].toLowerCase() === 'kg') n *= 1000;
  return n > 0 ? n : null;
}

/**
 * Los gramos de un precio: el gramaje elegido, el escrito en «Otro», o en un pack los gramos de la
 * unidad (el gramaje del SKU) por cuántas trae.
 */
export function gramosModerno(pb: Json | null, grDefecto: string | null): number | null {
  if (pb === null) return null;
  const gramaje = str(pb.gramaje);
  if (gramaje === GR_PACK) {
    const u = gramosDeTexto(grDefecto);
    const n = num(pb.pack_unidades);
    return u !== null && n !== null && n > 0 ? u * n : null;
  }
  if (gramaje === GR_OTRO) return gramosDeTexto(pb.gramaje_otro);
  return gramaje === null ? null : gramosDeTexto(gramaje);
}

/** El precio que paga cualquiera: el de oferta si lo hay, si no el regular. Igual que el formulario. */
export const precioParaTodos = (pb: Json | null): number | null =>
  (pb === null ? null : num(pb.oferta) ?? num(pb.regular));

/**
 * El precio por 100 g. **No se pregunta: se calcula**, igual que en el celular, para que un dígito de
 * más se vea como una brecha absurda y no como un dato.
 */
export function por100Moderno(pb: Json | null, grDefecto: string | null): number | null {
  const g = gramosModerno(pb, grDefecto);
  const p = precioParaTodos(pb);
  return g !== null && p !== null ? (p / g) * 100 : null;
}

// ═══════════════════════════════════════════════════════════════════════════
// El choque de precio obligatorio (2.30 · 3.32)
// ═══════════════════════════════════════════════════════════════════════════

export type EstadoChoque =
  | 'no_medida' | 'sku_sin_estado' | 'no_se_pide' | 'sin_contestar' | 'rival_no_esta' | 'falta_precio'
  | 'completo';

export interface ChoqueCalc {
  estado: EstadoChoque;
  estadoPropio: string | null;
  estadoRival: string | null;
  propio100: number | null;
  rival100: number | null;
  brechaPct: number | null;
}

/**
 * Cómo quedó el choque obligatorio de una góndola.
 *
 * El formulario solo lo pide cuando el SKU propio quedó «Está»: si no está, «su ausencia ya está
 * dicha» en 2.9 / 3.10 y el choque **no se pide**, que no es lo mismo que no contestado.
 */
export function choqueObligatorio(gd: Json | null, g: GondolaYi): ChoqueCalc {
  const base: ChoqueCalc = {
    estado: 'no_medida', estadoPropio: null, estadoRival: null, propio100: null, rival100: null, brechaPct: null,
  };
  if (!gondolaMedida(gd)) return base;
  const ch = obj(gd?.m10_choque_obligatorio);
  const estadoPropio = estadoSku(gd, g.choque.sku) ?? str(ch?.estado_propio);
  if (ch?.no_se_pide === true || (estadoPropio !== null && estadoPropio !== 'Está')) {
    return { ...base, estado: 'no_se_pide', estadoPropio };
  }
  // Sin el estado del SKU propio el choque no se pudo ni pedir: es un hueco de 2.9, no de 2.30.
  if (estadoPropio === null) return { ...base, estado: 'sku_sin_estado' };
  const estadoRival = str(ch?.estado_rival);
  if (estadoRival === null) return { ...base, estado: 'sin_contestar', estadoPropio };
  if (estadoRival !== 'Sí, está') return { ...base, estado: 'rival_no_esta', estadoPropio, estadoRival };

  const sku = g.nucleo.find((s) => s.k === g.choque.sku);
  const propio100 = por100Moderno(obj(obj(gd?.m10_precios)?.[g.choque.sku]), sku?.gr ?? null);
  const rival100 = por100Moderno(obj(ch?.rival), g.choque.grRival);
  if (propio100 === null || rival100 === null || rival100 <= 0) {
    return { ...base, estado: 'falta_precio', estadoPropio, estadoRival, propio100, rival100 };
  }
  return {
    estado: 'completo', estadoPropio, estadoRival, propio100, rival100, brechaPct: brecha(propio100, rival100),
  };
}

/** La brecha de un choque adicional (2.31 · 3.33) contra el precio de su SKU propio. */
export function brechaAdicional(
  gd: Json | null, g: GondolaYi, x: Json,
): { propio100: number | null; rival100: number | null; brechaPct: number | null } {
  const def = g.choquesExtra.find((p) => p.par === str(x.par));
  if (def === undefined) return { propio100: null, rival100: null, brechaPct: null };
  const sku = g.nucleo.find((s) => s.k === def.sku);
  const propio100 = por100Moderno(obj(obj(gd?.m10_precios)?.[def.sku]), sku?.gr ?? null);
  const rival100 = por100Moderno(obj(x.rival), def.gr);
  return {
    propio100, rival100,
    brechaPct: propio100 !== null && rival100 !== null && rival100 > 0 ? brecha(propio100, rival100) : null,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// De qué marca es cada cosa de la vuelta
// ═══════════════════════════════════════════════════════════════════════════

/** Una exhibición de la vuelta es de la marca cuyo lado de la góndola ocupa, o de ninguna. */
export function ladoDeExhibicion(e: Json): MarcaCliente | null {
  const marca = str(e.marca);
  if (marca === null) return null;
  return ladoDeMarca(marca) ?? ladoDeMarca(str(e.marca_otra));
}

export const exhibicionesDe = (f: FichaYiModerno): Json[] =>
  arr(en(f.ficha, 'vuelta', 'c1_exhibiciones')).map(obj).filter((x): x is Json => x !== null);

// ═══════════════════════════════════════════════════════════════════════════
// ¿La marca estaba?
// ═══════════════════════════════════════════════════════════════════════════

/**
 * ¿Hay en la ficha alguna prueba de que la marca estaba en la tienda?
 *
 * Cuenta como prueba —cualquiera alcanza—:
 * - un SKU núcleo en «Está» o «Solo en exhibición»;
 * - alguna «otra presentación» tocada (2.11 · 3.12: se tocan las que se VEN), o un producto propio
 *   fuera de la lista escrito en 2.12 · 3.13;
 * - celdas pintadas de la marca en la cuadrícula;
 * - Pringles: frentes de lata de Pringles mayores que cero, o un lugar donde se la encontró (2.2),
 *   o cajas con Pringles (4.8);
 * - Kellogg's: un bloque de Kellogg's junto o partido (3.9), o repisas marcadas para el bloque de
 *   niños o para Corn Flakes (3.7, 3.8);
 * - una exhibición de la marca en la vuelta que estaba llena o a medias. Una exhibición vacía no
 *   prueba que haya producto.
 *
 * La publicidad del camino (1.1) NO es prueba: un cartel no es un producto en la góndola.
 */
export function hayEvidenciaModerno(f: FichaYiModerno, marca: MarcaCliente): boolean {
  const g = GONDOLA_DE_MARCA[marca];
  const gd = gondolaDe(f, g);
  if (gondolaMedida(gd)) {
    if (g.nucleo.some((s) => SKU_ESTA.includes(estadoSku(gd, s.k) ?? ''))) return true;
    if (strs(gd?.m6_otras_presentaciones).length > 0) return true;
    // 2.12 · 3.13: «¿Viste algún producto de la marca que no esté en la lista?». Si se escribió, se vio.
    if (str(gd?.m6_otros_propios_texto) !== null) return true;
    const r = reparto(leerEspacio(gd));
    if (r.marcas.some((m) => m.marca === g.marcaNom && m.celdas > 0)) return true;
    if (marca === 'pringles') {
      const fr = num(en(gd, 'm4_frentes_latas', 'pringles'));
      if (fr !== null && fr > 0) return true;
      if (strs(gd?.m2_donde_vive_pringles).some((x) => x !== 'No la encontré')) return true;
    } else {
      const bloque = str(gd?.m7_bloque_kelloggs);
      if (bloque !== null && bloque !== "No hay Kellogg's en la góndola") return true;
      if (strs(gd?.m7_bloque_ninos_repisas).some((x) => x !== 'No hay bloque de niños')) return true;
      if (strs(gd?.m7_corn_flakes_repisas).some((x) => x !== "No está Corn Flakes de Kellogg's")) return true;
    }
  }
  if (marca === 'pringles') {
    const cajas = num(en(f.ficha, 'vuelta', 'c2_cajas_con_pringles'));
    if (cajas !== null && cajas > 0) return true;
  }
  return exhibicionesDe(f).some((e) => str(e.marca) === g.marcaNom
    && ['Llena', 'A medias'].includes(str(e.lleno) ?? ''));
}

/** 4.6 · las dos respuestas que dicen que se recorrió TODA la tienda (`FIN_VUELTA[0]` y `[1]`). */
const VUELTA_COMPLETA: readonly string[] = [
  'Recorrí toda la tienda y registré las que había', 'Recorrí toda la tienda y no había ninguna',
];

/**
 * ¿Se miró el resto de la tienda, donde la marca podría estar fuera de su góndola?
 *
 * La vuelta (4.6) tiene que haber recorrido toda la tienda: una exhibición de la marca en un pasillo
 * que no se visitó es una prueba que falta, no una ausencia. Para Pringles, además, la zona de cajas
 * (4.8) tiene que estar contada: la lata chica vive muchas veces en la caja y no en el pasillo.
 */
function restoDeLaTiendaMirado(f: FichaYiModerno, marca: MarcaCliente): boolean {
  if (!VUELTA_COMPLETA.includes(str(en(f.ficha, 'vuelta', 'c1_como_termino_vuelta')) ?? '')) return false;
  if (marca === 'pringles' && num(en(f.ficha, 'vuelta', 'c2_cajas_con_pringles')) === null) return false;
  return true;
}

/**
 * ¿Se encontró la marca en esta tienda?
 *
 * - **presente**: hay alguna prueba (ver `hayEvidenciaModerno`).
 * - **ausente**, solo en dos casos que la ficha dice sin lugar a duda, y **siempre que el resto de la
 *   tienda se haya mirado** (la vuelta completa y, para Pringles, las cajas contadas):
 *   1. la góndola «No existe en esta tienda»;
 *   2. la góndola se midió «Completa», **todos** los SKU núcleo quedaron en «Cenefa sin producto» o
 *      «Ni cenefa ni producto», y en 2.16 / 3.18 dice «Sí, todas» (sin eso, que no se haya tocado
 *      ninguna otra presentación puede ser «no lo miré»).
 * - **no se sabe**: todo lo demás. «En reposición», «No me dejaron», «A medias» sin prueba, un SKU en
 *   «No lo pude revisar», una góndola sin contestar, una vuelta que no recorrió toda la tienda. **No
 *   se cuenta como ausencia**: un «no está» que nadie midió es un cero inventado.
 */
export function presenciaModerno(f: FichaYiModerno, marca: MarcaCliente): Presencia {
  if (hayEvidenciaModerno(f, marca)) return 'presente';
  if (!restoDeLaTiendaMirado(f, marca)) return 'no_se_sabe';
  const g = GONDOLA_DE_MARCA[marca];
  const gd = gondolaDe(f, g);
  const puerta = puertaDe(gd);
  if (puerta === 'No existe en esta tienda') return 'ausente';
  if (puerta === 'Completa'
    && g.nucleo.every((s) => SKU_NO_ESTA.includes(estadoSku(gd, s.k) ?? ''))
    && str(gd?.m6_revision_listas) === 'Sí, todas') return 'ausente';
  return 'no_se_sabe';
}

// ═══════════════════════════════════════════════════════════════════════════
// Lo que se ve plegado: «qué se midió»
// ═══════════════════════════════════════════════════════════════════════════

/** Una línea por marca para la columna «Qué se midió», sin abrir la ficha. */
export function queSeMidioModerno(f: FichaYiModerno, marca: MarcaCliente): string {
  const g = GONDOLA_DE_MARCA[marca];
  const gd = gondolaDe(f, g);
  const puerta = puertaDe(gd);
  const partes: string[] = [];
  if (puerta === null) partes.push('góndola sin contestar');
  else if (!gondolaMedida(gd)) partes.push(puerta);
  else {
    const esta = g.nucleo.filter((s) => estadoSku(gd, s.k) === 'Está').length;
    partes.push(puerta);
    partes.push(`${esta} de ${g.nucleo.length} SKU «Está»`);
    const p = participacionCliente(gd, g);
    partes.push(p === null ? 'sin espacio pintado' : `${p} % del espacio`);
    if (marca === 'pringles') {
      const fr = num(en(gd, 'm4_frentes_latas', 'pringles'));
      if (fr !== null) partes.push(`${fr} frentes de lata`);
    }
    const precios = Object.keys(obj(gd?.m10_precios) ?? {}).length;
    partes.push(`${precios} ${precios === 1 ? 'precio' : 'precios'}`);
  }
  if (marca === 'pringles') {
    const abiertas = num(en(f.ficha, 'vuelta', 'c2_cajas_abiertas'));
    const conP = num(en(f.ficha, 'vuelta', 'c2_cajas_con_pringles'));
    if (abiertas !== null && conP !== null) partes.push(`cajas: ${conP} de ${abiertas} con Pringles`);
  }
  return partes.join(' · ');
}

// ═══════════════════════════════════════════════════════════════════════════
// El resumen
// ═══════════════════════════════════════════════════════════════════════════

export const SIN_CONTESTAR = 'Sin contestar';

export interface ResumenSku {
  k: string;
  nom: string;
  /** Una cuenta por cada estado de 2.9 / 3.10, en su orden, más «Sin contestar». */
  conteo: Conteo[];
}

export interface ResumenGondola {
  g: GondolaYi;
  fichas: number;
  /** 2.1 / 3.1, sobre todas las fichas. */
  puerta: Conteo[];
  medidas: number;
  /** El estado de cada SKU núcleo, sobre las góndolas medidas. */
  skus: ResumenSku[];
  /** El promedio por tienda de la participación de cada marca, sobre las que tienen espacio pintado. */
  espacio: { conEspacio: number; marcas: { marca: string; pct: number }[] };
  /** Solo snacks: frentes de lata por marca. */
  frentes: {
    conNumeros: number;
    sinTramo: number;
    sinContestar: number;
    porMarca: { k: string; nom: string; total: number }[];
    pctCliente: number | null;
  } | null;
  /** 2.10 / 3.11, sobre los SKU en «Cenefa sin producto». */
  almacen: { quiebres: number; conteo: Conteo[] };
  choque: {
    /** Góndolas donde el SKU propio quedó «Está»: las únicas donde el formulario pide el choque. */
    pedidos: number;
    noSePide: number;
    /** El SKU propio quedó sin estado: el choque no se pudo ni pedir. */
    skuSinEstado: number;
    sinContestar: number;
    rivalNoEsta: Conteo[];
    faltaPrecio: number;
    brechas: ResumenBrechas;
  };
  /** Solo cereales: 3.9. */
  bloque: Conteo[] | null;
  material: { conMaterial: number; sinMaterial: number; sinContestar: number; piezasPorMarca: Conteo[] };
  promocion: { conCarteles: number; sinCarteles: number; sinContestar: number; ofrece: Conteo[] };
  impulsadora: { contestaron: number; conteo: Conteo[] };
  /** 1.1: si se vio publicidad DE ESTA MARCA en el camino al pasillo. */
  camino: { contestaron: number; conPublicidad: number };
}

const marcaEscrita = (x: Json): string =>
  (str(x.marca) === 'Otra · la escribo' ? (str(x.marca_otra) ?? 'Otra, sin escribir') : (str(x.marca) ?? SIN_CONTESTAR));

export function resumenGondola(fichas: readonly FichaYiModerno[], marca: MarcaCliente): ResumenGondola {
  const g = GONDOLA_DE_MARCA[marca];
  const gds = fichas.map((f) => gondolaDe(f, g));
  const medidas = gds.filter(gondolaMedida);

  const puerta = contarEnOrden(gds.map((gd) => puertaDe(gd) ?? SIN_CONTESTAR), [...GATE, SIN_CONTESTAR]);

  const skus = g.nucleo.map((s) => ({
    k: s.k, nom: s.nom,
    conteo: contarEnOrden(medidas.map((gd) => estadoSku(gd, s.k) ?? SIN_CONTESTAR), [...ESTADO_SKU, SIN_CONTESTAR]),
  }));

  // Por tienda y no sumando celdas: sumando, la tienda con la góndola más ancha valdría por tres.
  const repartos = medidas.map((gd) => reparto(leerEspacio(gd))).filter((r) => r.medida);
  const nombres = [...new Set(repartos.flatMap((r) => r.marcas.map((m) => m.marca)))];
  const espacio = {
    conEspacio: repartos.length,
    marcas: nombres.map((m) => ({
      marca: m,
      pct: Math.round(repartos.reduce((s, r) => s + (r.marcas.find((x) => x.marca === m)?.pct ?? 0), 0)
        / repartos.length),
    })),
  };

  let frentes: ResumenGondola['frentes'] = null;
  if (g.frentes !== null) {
    const lista = g.frentes;
    const conNum = medidas.filter((gd) => str(gd?.m4_frentes_estado) === null
      && lista.every(([k]) => num(en(gd, 'm4_frentes_latas', k)) !== null));
    const porMarca = lista.map(([k, nom]) => ({
      k, nom, total: conNum.reduce((s, gd) => s + (num(en(gd, 'm4_frentes_latas', k)) ?? 0), 0),
    }));
    const total = porMarca.reduce((s, x) => s + x.total, 0);
    frentes = {
      conNumeros: conNum.length,
      sinTramo: medidas.filter((gd) => str(gd?.m4_frentes_estado) !== null).length,
      sinContestar: medidas.length - conNum.length
        - medidas.filter((gd) => str(gd?.m4_frentes_estado) !== null).length,
      porMarca,
      pctCliente: total === 0 ? null : Math.round(((porMarca.find((x) => x.k === 'pringles')?.total ?? 0) / total) * 100),
    };
  }

  const quiebres = medidas.flatMap((gd) => g.nucleo
    .filter((s) => estadoSku(gd, s.k) === 'Cenefa sin producto')
    .map((s) => str(obj(gd?.m13_almacen_segun_personal)?.[s.k]) ?? SIN_CONTESTAR));

  const choques = medidas.map((gd) => choqueObligatorio(gd, g));

  const material = {
    conMaterial: medidas.filter((gd) => arr(gd?.m8_piezas).length > 0).length,
    sinMaterial: medidas.filter((gd) => str(gd?.m8_sin_material) !== null).length,
    sinContestar: medidas.filter((gd) => arr(gd?.m8_piezas).length === 0 && str(gd?.m8_sin_material) === null).length,
    piezasPorMarca: contar(medidas.flatMap((gd) => arr(gd?.m8_piezas).map(obj)
      .filter((x): x is Json => x !== null).map(marcaEscrita))),
  };
  const promocion = {
    conCarteles: medidas.filter((gd) => arr(gd?.m9_carteles).length > 0).length,
    sinCarteles: medidas.filter((gd) => str(gd?.m9_sin_carteles) !== null).length,
    sinContestar: medidas.filter((gd) => arr(gd?.m9_carteles).length === 0 && str(gd?.m9_sin_carteles) === null).length,
    ofrece: contar(medidas.flatMap((gd) => arr(gd?.m9_carteles).map(obj)
      .filter((x): x is Json => x !== null)
      .map((x) => (str(x.ofrece) === 'Otra' ? (str(x.ofrece_otra) ?? 'Otra') : (str(x.ofrece) ?? SIN_CONTESTAR))))),
  };
  const conImpul = medidas.filter((gd) => strs(gd?.m11_impulsadora).length > 0);

  const caminos = fichas.map((f) => strs(en(f.ficha, 'camino', 'm0_publicidad_camino')));
  const contestaron = caminos.filter((c) => c.length > 0);

  return {
    g,
    fichas: fichas.length,
    puerta,
    medidas: medidas.length,
    skus,
    espacio,
    frentes,
    almacen: { quiebres: quiebres.length, conteo: contar(quiebres) },
    choque: {
      pedidos: choques.filter((c) => !['no_medida', 'no_se_pide', 'sku_sin_estado'].includes(c.estado)).length,
      noSePide: choques.filter((c) => c.estado === 'no_se_pide').length,
      skuSinEstado: choques.filter((c) => c.estado === 'sku_sin_estado').length,
      sinContestar: choques.filter((c) => c.estado === 'sin_contestar').length,
      rivalNoEsta: contarEnOrden(
        choques.filter((c) => c.estado === 'rival_no_esta').map((c) => c.estadoRival ?? SIN_CONTESTAR),
        ESTADO_RIVAL.slice(1),
      ).filter((c) => c.n > 0),
      faltaPrecio: choques.filter((c) => c.estado === 'falta_precio').length,
      brechas: resumirBrechas(choques.map((c) => c.brechaPct).filter((b): b is number => b !== null)),
    },
    bloque: marca === 'kelloggs'
      ? contar(medidas.map((gd) => str(gd?.m7_bloque_kelloggs) ?? SIN_CONTESTAR))
      : null,
    material,
    promocion,
    impulsadora: { contestaron: conImpul.length, conteo: contar(conImpul.flatMap((gd) => strs(gd?.m11_impulsadora))) },
    camino: {
      contestaron: contestaron.length,
      conPublicidad: contestaron.filter((c) => c.includes(CAMINO_DE_MARCA[marca])).length,
    },
  };
}

export interface ResumenCajas {
  /** Fichas que contaron las cajas abiertas Y las cajas con Pringles. */
  contaron: number;
  abiertas: number;
  conPringles: number;
  /** Sobre las que además contestaron 4.10. */
  contaronOtraPapa: number;
  abiertasOtraPapa: number;
  conOtraPapa: number;
  /** 4.9, multirrespuesta: el denominador son las TIENDAS que contestaron, no la suma de marcas. */
  contaronFormato: number;
  formatos: Conteo[];
}

/** La zona de cajas (4.7 a 4.12): toda es de Pringles. */
export function resumenCajas(fichas: readonly FichaYiModerno[]): ResumenCajas {
  const vueltas = fichas.map((f) => obj(f.ficha.vuelta));
  const contaron = vueltas.filter((v) => num(v?.c2_cajas_abiertas) !== null && num(v?.c2_cajas_con_pringles) !== null);
  const otra = contaron.filter((v) => num(v?.c2_cajas_con_otra_papa) !== null);
  return {
    contaron: contaron.length,
    abiertas: contaron.reduce((s, v) => s + (num(v?.c2_cajas_abiertas) ?? 0), 0),
    conPringles: contaron.reduce((s, v) => s + (num(v?.c2_cajas_con_pringles) ?? 0), 0),
    contaronOtraPapa: otra.length,
    abiertasOtraPapa: otra.reduce((s, v) => s + (num(v?.c2_cajas_abiertas) ?? 0), 0),
    conOtraPapa: otra.reduce((s, v) => s + (num(v?.c2_cajas_con_otra_papa) ?? 0), 0),
    contaronFormato: vueltas.filter((v) => strs(v?.c2_formato_pringles_caja).length > 0).length,
    formatos: contar(vueltas.flatMap((v) => strs(v?.c2_formato_pringles_caja))),
  };
}

export interface ResumenExhibiciones {
  total: number;
  porMarca: Conteo[];
  porLleno: Conteo[];
  /** 4.6, sobre todas las fichas: separa «no había» de «no se miró». */
  termino: Conteo[];
}

/**
 * Las exhibiciones fuera del pasillo. Con una marca, solo las de su lado de la góndola: las de la
 * marca y las de su competencia. Las que no son de ninguna de las dos categorías («Marca del súper»,
 * «Otra» sin escribir) quedan fuera del enfoque y se cuentan en «Las dos».
 */
export function resumenExhibiciones(fichas: readonly FichaYiModerno[], marca: MarcaCliente | null): ResumenExhibiciones {
  const todas = fichas.flatMap(exhibicionesDe);
  const lista = marca === null ? todas : todas.filter((e) => ladoDeExhibicion(e) === marca);
  return {
    total: lista.length,
    porMarca: contar(lista.map(marcaEscrita)),
    porLleno: contar(lista.map((e) => str(e.lleno) ?? SIN_CONTESTAR)),
    termino: contar(fichas.map((f) => str(en(f.ficha, 'vuelta', 'c1_como_termino_vuelta')) ?? SIN_CONTESTAR)),
  };
}

/** 1.1 derivada para una marca: si se vio su publicidad, si no, o si no se contestó. */
export function caminoDeMarca(f: FichaYiModerno, marca: MarcaCliente): 'si' | 'no' | null {
  const c = strs(en(f.ficha, 'camino', 'm0_publicidad_camino'));
  if (c.length === 0) return null;
  // Contestó y no marcó esta marca: o «No vi nada», o vio solo la de la otra marca. Las dos son «no».
  return c.includes(CAMINO_DE_MARCA[marca]) ? 'si' : 'no';
}
